import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import { FullPageLoading } from "../../components/Loading";
import { Eyebrow, HeadingItalic } from "../../components/design";
import {
  adminGetChapter,
  adminGetUser,
  adminListChapters,
  type ChapterDetail,
  type ChapterSummary,
} from "../../lib/api";
import { chapterNumber, chapterTitleWithoutNumber, toRoman } from "../../lib/format";

// Admin-scoped plain-text view of the full biography. Fetches every chapter
// that has content and stitches it together for reading or copy-paste.

export default function AdminBookText() {
  const { phone = "" } = useParams<{ phone: string }>();
  const decodedPhone = decodeURIComponent(phone);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState<string>("");
  const [chapters, setChapters] = useState<
    { num: number; summary: ChapterSummary; detail: ChapterDetail }[]
  >([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const userP = adminGetUser(decodedPhone).catch(() => null);
        const listP = adminListChapters(decodedPhone);
        const [u, listResp] = await Promise.all([userP, listP]);

        if (u) {
          const d: any = (u as any).data || {};
          setName(
            [d.interviewee_first_name, d.interviewee_last_name]
              .filter(Boolean)
              .join(" ") || decodedPhone,
          );
        }

        const sorted = [...(listResp.chapters || [])].sort(
          (a, b) => chapterNumber(a.title) - chapterNumber(b.title),
        );
        const withContent = sorted
          .map((c, idx) => ({ c, num: idx + 1 }))
          .filter(({ c }) => (c.content_length || 0) > 0);

        const details = await Promise.all(
          withContent.map(({ c }) => adminGetChapter(decodedPhone, c.title)),
        );

        setChapters(
          withContent.map(({ c, num }, i) => ({
            num,
            summary: c,
            detail: details[i],
          })),
        );
        setError(null);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Konnte das Buch nicht laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [decodedPhone]);

  if (loading) return <FullPageLoading label="Lade Buchtext …" />;

  function buildPlainText(): string {
    const lines: string[] = [];
    lines.push(name ? `Lebensgeschichte von ${name}` : "Lebensgeschichte");
    lines.push("");
    for (const c of chapters) {
      lines.push(
        `${toRoman(c.num)} — ${chapterTitleWithoutNumber(c.detail.title) || c.detail.title}`,
      );
      lines.push("");
      lines.push((c.detail.content || "").trim());
      lines.push("");
      lines.push("");
    }
    return lines.join("\n").trim();
  }

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }

  const empty = chapters.length === 0;

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />

      {/* Sticky admin banner with back nav. */}
      <div
        style={{
          background: "var(--oxblood)",
          color: "var(--paper-50)",
          padding: "10px 22px",
          fontFamily: "var(--f-ui)",
          fontSize: 13,
          position: "sticky",
          top: 0,
          zIndex: 50,
          boxShadow: "0 2px 8px rgba(43,26,15,0.18)",
        }}
      >
        <div
          className="desk-container-app"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>
            <strong
              style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
            >
              Admin-Vorschau
            </strong>{" "}
            · Buchtext von {name || decodedPhone}
          </span>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Link
              to={`/admin/users/${encodeURIComponent(decodedPhone)}/preview`}
              style={{
                color: "var(--paper-50)",
                textDecoration: "underline",
                textUnderlineOffset: 4,
              }}
            >
              ← Zurück zur Vorschau
            </Link>
            <Link
              to={`/admin/users/${encodeURIComponent(decodedPhone)}`}
              style={{
                color: "var(--paper-50)",
                textDecoration: "underline",
                textUnderlineOffset: 4,
                opacity: 0.85,
              }}
            >
              Detail
            </Link>
          </div>
        </div>
      </div>

      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "32px 22px 80px",
        }}
      >
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(184,52,40,0.08)",
              border: "1px solid rgba(184,52,40,0.30)",
              borderRadius: 8,
              padding: "12px 14px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--error)",
              marginBottom: 20,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <Eyebrow>Buchtext</Eyebrow>
            <HeadingItalic size={28}>
              <em>{name || decodedPhone}</em>
            </HeadingItalic>
          </div>
          {!empty && (
            <button
              type="button"
              onClick={copyAll}
              className="btn-secondary"
              style={{
                padding: "8px 14px",
                minHeight: 0,
                fontSize: 13,
                whiteSpace: "nowrap",
              }}
            >
              {copied ? "Kopiert ✓" : "Alles kopieren"}
            </button>
          )}
        </div>

        {empty ? (
          <div
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 12,
              padding: "20px 22px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 15,
              color: "var(--ink-soft)",
            }}
          >
            Noch keine Kapitel mit Inhalt.
          </div>
        ) : (
          chapters.map((c) => (
            <article
              key={c.detail.title}
              style={{
                background: "var(--paper-50)",
                border: "1px solid rgba(43,26,15,0.10)",
                borderRadius: 4,
                padding: "26px 26px 22px",
                marginBottom: 22,
                boxShadow: "0 10px 22px -12px rgba(43,26,15,0.14)",
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontStyle: "italic",
                    fontSize: 36,
                    lineHeight: 1,
                    color: "var(--oxblood)",
                    marginBottom: 4,
                  }}
                >
                  {toRoman(c.num)}
                </div>
                <h2
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 22,
                    margin: 0,
                    color: "var(--ink)",
                  }}
                >
                  {chapterTitleWithoutNumber(c.detail.title) || c.detail.title}
                </h2>
                <div
                  style={{
                    fontFamily: "var(--f-ui)",
                    fontSize: 11,
                    color: "var(--ink-faint)",
                    marginTop: 4,
                  }}
                >
                  {c.summary.content_length} Zeichen · Sättigung{" "}
                  {c.summary.saturation_score || 0} %
                </div>
              </div>

              {(c.detail.content || "")
                .split(/\n\s*\n+/g)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p, i) => (
                  <p
                    key={i}
                    style={{
                      fontFamily: "var(--f-body)",
                      fontSize: 16,
                      lineHeight: 1.55,
                      color: "var(--ink)",
                      margin: "0 0 14px",
                    }}
                  >
                    {p}
                  </p>
                ))}
            </article>
          ))
        )}
      </main>
    </div>
  );
}

